import { useEffect, useMemo, useState } from 'react';
import { fetchPlanungsAmpeln } from '../../services/planungAmpelService';
import type { PlanungAmpel } from '../planungsphase/planungAmpel';
import { lesefehlerText } from '../planungsphase/planungKachel';
import type { AmpelProjekt } from './planungHandlungsbedarf';

/**
 * Planungs-Ampeln fuer alle Projekte einer Liste (Dashboard, Aufgabenliste) —
 * EINE Sammelabfrage (`fetchPlanungsAmpeln`, S1-c), nicht eine je Projekt.
 *
 * Fail-open: scheitert die Abfrage, bleiben die Ampeln leer und `fehler`
 * traegt den Grund; die Anzeige sagt dann „nicht lesbar" statt zu schweigen.
 * `weiter` (Leitfaden, Stufe L3) kommt aus derselben Abfrage und gehoert
 * immer zum selben Lauf wie `ampeln`.
 *
 * Der Aufrufer memoisiert `eingaenge`; der Effekt haengt zusaetzlich an einem
 * Schluessel aus den Eingaben, damit ein blosses Neuladen der Projektliste mit
 * unveraendertem Inhalt den alten Stand weiterzeigt, statt die Liste leer zu
 * blinken.
 */
export interface PlanungAmpelnStand {
    ampeln: Readonly<Record<string, PlanungAmpel>>;
    /** Weiter-Text je Projekt (`PlanungsAmpeln.weiter`); fehlt einer, leer. */
    weiter: Readonly<Record<string, string>>;
    laden: boolean;
    fehler: string | null;
}

const KEINE: Readonly<Record<string, PlanungAmpel>> = {};
const KEIN_WEITER: Readonly<Record<string, string>> = {};
const LEER: PlanungAmpelnStand = { ampeln: KEINE, weiter: KEIN_WEITER, laden: false, fehler: null };

interface Lauf {
    fuer: string;
    ampeln: Readonly<Record<string, PlanungAmpel>>;
    weiter: Readonly<Record<string, string>>;
    fehler: string | null;
}

export const usePlanungAmpeln = (eingaenge: ReadonlyArray<AmpelProjekt>, heuteIso: string): PlanungAmpelnStand => {
    const schluessel = useMemo(
        () => `${eingaenge.map(e => `${e.projectId}|${e.baustartIso ?? ''}|${e.lvHatPlanungsleistung ? 1 : 0}`).join(';')}#${heuteIso}`,
        [eingaenge, heuteIso],
    );
    const [stand, setStand] = useState<Lauf>({ fuer: '', ampeln: KEINE, weiter: KEIN_WEITER, fehler: null });

    useEffect(() => {
        if (eingaenge.length === 0) return;
        let abgebrochen = false;
        fetchPlanungsAmpeln([...eingaenge], heuteIso)
            .then(ergebnis => {
                if (abgebrochen) return;
                setStand({
                    fuer: schluessel,
                    ampeln: ergebnis.ampeln,
                    weiter: ergebnis.weiter || KEIN_WEITER,
                    fehler: lesefehlerText(ergebnis.lesefehler),
                });
            })
            .catch((e: unknown) => {
                if (abgebrochen) return;
                console.warn('Planungs-Ampeln konnten nicht geladen werden.', e);
                setStand({ fuer: schluessel, ampeln: KEINE, weiter: KEIN_WEITER, fehler: e instanceof Error ? e.message : String(e) });
            });
        return () => { abgebrochen = true; };
    }, [eingaenge, heuteIso, schluessel]);

    if (eingaenge.length === 0) return LEER;
    const aktuell = stand.fuer === schluessel;
    // Waehrend ein neuer Stand laedt, bleibt der alte sichtbar — nur der
    // Lesefehler gehoert zum jeweiligen Lauf und wird nicht mitgeschleppt.
    return { ampeln: stand.ampeln, weiter: stand.weiter, laden: !aktuell, fehler: aktuell ? stand.fehler : null };
};
